import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  makeStyles,
  Typography,
} from "@material-ui/core";
import { presentFormatedData } from "../../functionalComponents/dataFormatFunctions";
import moment from "moment";

const useStyles = makeStyles({
  tableContainer: {
    marginTop: "20px",
    border: "1px solid #ccc",
    borderRadius: "8px",
    overflowX: "auto",
  },
  headerCell: {
    fontWeight: "bold",
    backgroundColor: "#f5f5f5",
    padding: "12px 16px",
    whiteSpace: "nowrap",
  },
  bodyCell: {
    padding: "12px 16px",
    verticalAlign: "top",
  },
  timestamp: {
    fontSize: "11px",
    color: "#666",
    marginTop: "2px",
    fontStyle: "italic",
  },
  amountRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: "24px",
    paddingBottom: "4px",
    marginBottom: "4px",
    borderBottom: "1px dashed #e0e0e0",
    whiteSpace: "nowrap",
  },
  amountTotal: {
    display: "flex",
    justifyContent: "space-between",
    gap: "24px",
    fontWeight: 600,
    whiteSpace: "nowrap",
  },
  statusLabel: {
    fontWeight: 600,
    color: "#2e7d32",
  },
});

const isPopulated = (val) => val !== null && val !== undefined && val !== "";
const fmtTs = (v) =>
  v && moment(v).isValid()
    ? moment(v).format("DD-MMM-YYYY, HH:mm:ss").toUpperCase()
    : v;

// Per-PO invoice demo data, hardcoded from the approved mock.
// Keyed by PO Number (= scheduleAgreementNumber). Common columns
// (Condition Pricing Unit / UoM / Currency) are constant across the mock.
const COMMON = { conditionPricingUnit: "1", uom: "GM", currency: "USD" };

const INVOICE_DATA = {
  "40002408": {
    ...COMMON,
    sponsorPrice: "4.2",
    cmoPrice: "",
    cmoInvoicePrice: "4",
    variance: "",
    invoiceNumber: "50042683",
    invoicedQuantity: "6410",
    totalAmount: "26922",
    receivedTimestamp: "2025-06-05 08:45:00",
    paidTimestamp: "2025-06-15 10:20:00",
  },
  "40002409": {
    ...COMMON,
    sponsorPrice: "4",
    cmoPrice: "",
    cmoInvoicePrice: "3",
    variance: "",
    invoiceNumber: "50042684",
    invoicedQuantity: "6070",
    totalAmount: "24040",
    receivedTimestamp: "2025-11-10 12:40:22",
    paidTimestamp: "2025-11-30 10:10:00",
  },
  "40002410": {
    ...COMMON,
    sponsorPrice: "4.2",
    cmoPrice: "",
    cmoInvoicePrice: "4",
    variance: "",
    invoiceNumber: "50042685",
    invoicedQuantity: "6070",
    totalAmount: "25494",
    receivedTimestamp: "2026-08-16 15:20:10",
    paidTimestamp: "",
  },
  "40002411": {
    ...COMMON,
    sponsorPrice: "4.2",
    cmoPrice: "4.2",
    cmoInvoicePrice: "",
    variance: "0",
    invoiceNumber: "",
    invoicedQuantity: "",
    totalAmount: "",
    receivedTimestamp: "",
    paidTimestamp: "",
  },
  "40002412": {
    ...COMMON,
    sponsorPrice: "4.2",
    cmoPrice: "4.2",
    cmoInvoicePrice: "",
    variance: "0",
    invoiceNumber: "",
    invoicedQuantity: "",
    totalAmount: "",
    receivedTimestamp: "",
    paidTimestamp: "",
  },
  "40002413": {
    ...COMMON,
    sponsorPrice: "4.2",
    cmoPrice: "",
    cmoInvoicePrice: "4.2",
    variance: "0",
    invoiceNumber: "50042688",
    invoicedQuantity: "6410",
    totalAmount: "26922",
    receivedTimestamp: "2026-08-18 10:12:40",
    paidTimestamp: "",
  },
  "40002414": {
    ...COMMON,
    sponsorPrice: "4.2",
    cmoPrice: "",
    cmoInvoicePrice: "4.2",
    variance: "0",
    invoiceNumber: "50042689",
    invoicedQuantity: "6070",
    totalAmount: "25494",
    receivedTimestamp: "2026-08-18 10:12:40",
    paidTimestamp: "",
  },
};

// Deterministic seeded PRNG so a given PO always renders the SAME "random"
// values (stable across re-renders), yet different POs look different.
const seedFrom = (str) => {
  let h = 2166136261;
  for (let i = 0; i < str.length; i += 1) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
};
const mulberry32 = (seed) => () => {
  let t = (seed += 0x6d2b79f5);
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};
const pad2 = (n) => String(n).padStart(2, "0");

// Build unique-but-similar demo data for any PO not in the mock above.
// Shaped like the mock rows: GM / USD / prices ~3–4.5, qty ~6000–6500.
const generateInvoice = (poNumber) => {
  const rand = mulberry32(seedFrom(String(poNumber)));
  const pick = (min, max) => min + rand() * (max - min);

  const sponsorPrice = pick(3.8, 4.4).toFixed(1);
  const cmoInvoicePrice = pick(3, 4.5).toFixed(1);
  const invoicedQuantity = String(Math.round(pick(6000, 6500)));
  const totalAmount = String(
    Math.round(Number(cmoInvoicePrice) * Number(invoicedQuantity))
  );
  const invoiceNumber = String(50042690 + Math.floor(rand() * 9000));

  // ~30% of generated POs are still awaiting the invoice.
  const received = rand() > 0.3;
  const paid = received && rand() > 0.5;

  const year = 2025 + Math.floor(rand() * 2);
  const month = 1 + Math.floor(rand() * 12);
  const day = 1 + Math.floor(rand() * 28);
  const recTs = `${year}-${pad2(month)}-${pad2(day)} ${pad2(
    8 + Math.floor(rand() * 10)
  )}:${pad2(Math.floor(rand() * 60))}:${pad2(Math.floor(rand() * 60))}`;
  const paidTs = `${year}-${pad2(Math.min(12, month + 1))}-${pad2(
    day
  )} ${pad2(8 + Math.floor(rand() * 10))}:${pad2(
    Math.floor(rand() * 60)
  )}:00`;

  return {
    ...COMMON,
    sponsorPrice,
    cmoPrice: received ? "" : pick(3.8, 4.4).toFixed(1),
    cmoInvoicePrice: received ? cmoInvoicePrice : "",
    variance: "0",
    invoiceNumber: received ? invoiceNumber : "",
    invoicedQuantity: received ? invoicedQuantity : "",
    totalAmount: received ? totalAmount : "",
    receivedTimestamp: received ? recTs : "",
    paidTimestamp: paid ? paidTs : "",
  };
};

const InvoiceTable = ({ data } = {}) => {
  const classes = useStyles();

  // PO number of the selected schedule line.
  const poNumber = data?.scheduleLineDetails?.scheduleAgreementNumber;

  // Pick the row hardcoded from the mock; other POs get unique-but-similar
  // data generated deterministically from the PO number.
  const key = poNumber ? String(poNumber).trim() : "";
  const record =
    (key && INVOICE_DATA[key]) ||
    generateInvoice(key || "40000000");

  const {
    sponsorPrice,
    cmoPrice,
    cmoInvoicePrice,
    conditionPricingUnit,
    uom,
    currency,
    variance,
    invoiceNumber,
    invoicedQuantity,
    totalAmount,
    receivedTimestamp,
    paidTimestamp,
  } = record;

  const cmoPriceTs = "";
  // The mock reports a single Total Amount; the breakdown mirrors it with no
  // tax/discount applied.
  const priceOfGoods = totalAmount;
  const salesTax = "0";
  const discount = "0";

  // Invoice lifecycle state.
  // received: an invoice number has been ingested → CMO Price disappears,
  //           CMO Invoice Price / Invoice no. / Qty / Total Amount appear.
  // paid:     a paid timestamp exists → status shows both Received and Paid.
  const invoiceReceived = isPopulated(invoiceNumber);
  const invoicePaid = invoiceReceived && isPopulated(paidTimestamp);

  return (
    <TableContainer component={Paper} className={classes.tableContainer}>
      <Table className="default-table-styles">
        <TableHead>
          <TableRow>
            <TableCell className={classes.headerCell}>
              Sponsor Price
              <br />
              per Unit
            </TableCell>
            <TableCell className={classes.headerCell}>
              CMO Price
              <br />
              per Unit
            </TableCell>
            <TableCell className={classes.headerCell}>
              CMO Invoice
              <br />
              Price per Unit
            </TableCell>
            <TableCell className={classes.headerCell}>
              Condition Pricing
              <br />
              Unit
            </TableCell>
            <TableCell className={classes.headerCell}>UoM</TableCell>
            <TableCell className={classes.headerCell}>Currency</TableCell>
            <TableCell className={classes.headerCell}>Variance (%)</TableCell>
            <TableCell className={classes.headerCell}>Invoice no.</TableCell>
            <TableCell className={classes.headerCell}>Invoiced Qty.</TableCell>
            <TableCell className={classes.headerCell}>Total Amount</TableCell>
            <TableCell className={classes.headerCell}>Invoice Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            {/* Sponsor Price per Unit — always shown */}
            <TableCell className={classes.bodyCell}>
              {presentFormatedData(sponsorPrice)}
            </TableCell>

            {/* CMO Price per Unit — disappears once the invoice is received */}
            <TableCell className={classes.bodyCell}>
              {!invoiceReceived ? (
                <>
                  {presentFormatedData(cmoPrice)}
                  {isPopulated(cmoPriceTs) && (
                    <Typography className={classes.timestamp}>
                      {fmtTs(cmoPriceTs)}
                    </Typography>
                  )}
                </>
              ) : (
                ""
              )}
            </TableCell>

            {/* CMO Invoice Price per Unit — appears once the invoice is received */}
            <TableCell className={classes.bodyCell}>
              {invoiceReceived ? presentFormatedData(cmoInvoicePrice) : ""}
            </TableCell>

            {/* Condition Pricing Unit */}
            <TableCell className={classes.bodyCell}>
              {presentFormatedData(conditionPricingUnit)}
            </TableCell>

            {/* UoM */}
            <TableCell className={classes.bodyCell}>
              {presentFormatedData(uom)}
            </TableCell>

            {/* Currency */}
            <TableCell className={classes.bodyCell}>
              {presentFormatedData(currency)}
            </TableCell>

            {/* Variance (%) */}
            <TableCell className={classes.bodyCell}>
              {presentFormatedData(variance)}
            </TableCell>

            {/* Invoice no. — appears once the invoice is received */}
            <TableCell className={classes.bodyCell}>
              {invoiceReceived ? presentFormatedData(invoiceNumber) : ""}
            </TableCell>

            {/* Invoiced Qty. — appears once the invoice is received */}
            <TableCell className={classes.bodyCell}>
              {invoiceReceived ? presentFormatedData(invoicedQuantity) : ""}
            </TableCell>

            {/* Total Amount — breakdown appears once the invoice is received */}
            <TableCell className={classes.bodyCell}>
              {invoiceReceived ? (
                <>
                  <div className={classes.amountRow}>
                    <span>Price of Goods:</span>
                    <span>{presentFormatedData(priceOfGoods)}</span>
                  </div>
                  <div className={classes.amountRow}>
                    <span>Sales Tax:</span>
                    <span>{presentFormatedData(salesTax)}</span>
                  </div>
                  <div className={classes.amountRow}>
                    <span>Discount:</span>
                    <span>{presentFormatedData(discount)}</span>
                  </div>
                  <div className={classes.amountTotal}>
                    <span>Total Amount:</span>
                    <span>{presentFormatedData(totalAmount)}</span>
                  </div>
                </>
              ) : (
                ""
              )}
            </TableCell>

            {/* Invoice Status */}
            <TableCell className={classes.bodyCell}>
              {!invoiceReceived ? (
                "Not received yet"
              ) : (
                <>
                  <div>
                    <span className={classes.statusLabel}>Received</span>
                    {isPopulated(receivedTimestamp) && (
                      <Typography className={classes.timestamp}>
                        {fmtTs(receivedTimestamp)}
                      </Typography>
                    )}
                  </div>
                  {invoicePaid && (
                    <div style={{ marginTop: 8 }}>
                      <span className={classes.statusLabel}>Paid</span>
                      <Typography className={classes.timestamp}>
                        {fmtTs(paidTimestamp)}
                      </Typography>
                    </div>
                  )}
                </>
              )}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default InvoiceTable;
